import { isEmpty } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router";
import type { Search } from "react-router";

import {
  ENVIRONMENT_PRODUCTION,
  getAuthId,
  getAuthIdFromConfig,
  IntegrationAuthType,
  isExchangeCodeResponse,
  LS_FULL_STATE,
  LS_OAUTH_DATASOURCE_KEY,
  LS_OAUTH_ENV_KEY,
  LS_OAUTH_ONE_TIME_CODE,
  LS_OAUTH_REDIRECT_FINISHED,
} from "@superblocksteam/shared";
import type {
  Agent,
  AuthConfig,
  AuthId,
  AuthType,
  DatasourceAuthState,
  DatasourceOneTimeState,
  Organization,
  Profile,
  SupersetIntegrationDto,
  ViewMode,
} from "@superblocksteam/shared";

import { useFeatureFlag } from "hooks/ui/useFeatureFlag";
import { getAppViewMode } from "legacy/selectors/applicationSelectors";
import { selectActiveAgents } from "store/slices/agents";
import {
  exchangeCodeV3,
  loginDatasourceV3,
} from "store/slices/apisShared/client-auth";
import { Flag } from "store/slices/featureFlags";
import { selectOnlyOrganization } from "store/slices/organizations";
import { orgIsOnPremise } from "store/slices/organizations/utils";
import logger from "utils/logger";

import localStorage from "../../legacy/utils/localStorage";
import {
  resolveOAuthCallbackConfig,
  type ResolvedDatasourceConfig,
} from "./oauth-callback-config";
import {
  completeLocalStorageOAuthCallback,
  resolveOAuthCallbackHandoff,
  validateOAuthCallbackOneTimeCodeIfApplicable,
} from "./oauth-callback-handoff";
import {
  reportExchangeCodeFailure,
  requestTokenFromShell,
  resolveOAuthOpenerTargetOrigin,
  signalCompletionToShell,
  type ShellTokenReply,
} from "./oauth-callback-shell-handshake";
import {
  exchangeOAuthCallbackCode,
  fetchOAuthCallbackIntegration,
} from "./OAuthCallback.utils";

/**
 * How long to wait for the shell's postMessage reply. The shell handler
 * is synchronous and the window-to-window hop is microseconds; 2000ms
 * absorbs a slow shell bootstrap without keeping the user on
 * "Logging in..." longer than necessary.
 */
const SHELL_TOKEN_REQUEST_TIMEOUT_MS = 2000;

// This component is used to handle the OAuth callback from the OAuth provider(for new/existing Superblocks-managed OAuth clients and for new bring your own OAuth clients).
// For bring your own OAuth clients:
// - It will exchange the code for an access token and then close the window. The same can't be done for Superblocks-managed OAuth clients because we don't want to expose the client secret to the browser.
// For Superblocks-managed OAuth clients:
// - It will just close the window and update the local storage with status and error if any.
const OAuthCallback = () => {
  // State
  const location = useLocation();
  const hash = location.hash;
  const fragments = new URLSearchParams(hash.slice(1));

  // Either the token or the access code will be provided depending on the code
  // that was requested.
  const token = fragments.get("access_token");
  const expiresIn = fragments.get("expires_in");
  const expiryTimestamp = useMemo(() => {
    if (expiresIn) {
      return new Date().getTime() + parseInt(expiresIn) * 1000;
    }
    return undefined;
  }, [expiresIn]);

  const accessCode = new URLSearchParams(location.search).get("code");
  const paramError = new URLSearchParams(location.search).get("error");
  const authState = useMemo(
    () => getOAuthStateFromRedirect(location.search),
    [location.search],
  );
  const handoffMode = useMemo(
    () => resolveOAuthCallbackHandoff(authState),
    [authState],
  );
  const popupOrigin =
    typeof window !== "undefined" ? window.location.origin : "";
  const openerTargetOrigin = useMemo(
    () =>
      resolveOAuthOpenerTargetOrigin(
        (authState as DatasourceOneTimeState).openerOrigin,
        popupOrigin,
      ),
    [authState, popupOrigin],
  );
  const scope = new URLSearchParams(location.search).get("scope");
  const organization = useSelector(selectOnlyOrganization);
  // TODO DURING REVIEW: Why does this always use production agents?
  const datasourceToAuth = authState.integrationId;

  // Embed ExternalUser sessions (`externalUser` in OAuth state): the
  // shell supplies the runtime bearer via postMessage because
  // storage-partitioned embed contexts hide shell-seeded localStorage.
  const [shellTokenReply, setShellTokenReply] = useState<
    ShellTokenReply | undefined
  >();
  const [shellTokenResolved, setShellTokenResolved] = useState(false);
  useEffect(() => {
    if (handoffMode !== "postMessage" || !authState.externalUser) {
      setShellTokenResolved(true);
      return;
    }
    let cancelled = false;
    const opener =
      typeof window !== "undefined" ? (window.opener ?? null) : null;
    const oneTimeCode = authState.oneTimeCode;
    if (!opener || !oneTimeCode) {
      setShellTokenResolved(true);
      return;
    }
    requestTokenFromShell(
      { oneTimeCode, timeoutMs: SHELL_TOKEN_REQUEST_TIMEOUT_MS },
      {
        opener,
        windowTarget: window,
        popupOrigin,
        openerTargetOrigin,
        setTimeout: (cb, ms) => window.setTimeout(cb, ms),
        clearTimeout: (h) => window.clearTimeout(h as number),
      },
    )
      .then((reply) => {
        if (cancelled) return;
        setShellTokenReply(reply);
        setShellTokenResolved(true);
      })
      .catch(() => {
        if (cancelled) return;
        setShellTokenResolved(true);
      });
    return () => {
      cancelled = true;
    };
  }, [
    authState.externalUser,
    authState.oneTimeCode,
    handoffMode,
    openerTargetOrigin,
    popupOrigin,
  ]);

  const environment =
    handoffMode === "localStorage"
      ? (localStorage.getItem(LS_OAUTH_ENV_KEY) ?? ENVIRONMENT_PRODUCTION)
      : authState.externalUser
        ? (shellTokenReply?.profileKey ??
          authState.profileKey ??
          ENVIRONMENT_PRODUCTION)
        : (authState.profileKey ?? ENVIRONMENT_PRODUCTION);
  const oauthAccessToken =
    handoffMode === "postMessage" && authState.externalUser
      ? shellTokenReply?.token
      : undefined;
  const agents: Agent[] = useSelector(
    selectActiveAgents(organization?.agentType, environment),
  );

  const profile = organization?.profiles?.find((p) => p.key === environment);
  const profileId = useMemo(() => {
    return profile?.id ?? "";
  }, [profile?.id]);

  const [errorMessage, setErrorMessage] = useState<string>();
  const [isLoading, setIsLoading] = useState(true);
  // To prevent multiple requests from being sent in case the main useEffect is triggered multiple times.
  const sentRequest = useRef(false);
  const redirectFinishedRef = useRef(false);
  const mode = useSelector(getAppViewMode);
  const enableDataPlaneGateway = useFeatureFlag(Flag.ENABLE_DATA_PLANE_GATEWAY);

  const agentsLoading = useMemo(() => {
    const needsAgents = () => {
      // A helper to determine if we need agent URLs.
      // If control flow is disabled, we make agent requests to a known
      // address. No discovery needed.
      return orgIsOnPremise(organization);
    };

    // Agents are loading if we need them and they're not there.
    return needsAgents() && isEmpty(agents);
  }, [agents, organization]);

  // finish completes exactly one handoff mechanism: localStorage for ui-legacy
  // openers, postMessage for current UI / deployed-shell openers.
  const finish = useCallback(
    (error?: string) => {
      setIsLoading(false);
      redirectFinishedRef.current = true;

      if (handoffMode === "localStorage") {
        completeLocalStorageOAuthCallback({
          error,
          storage: localStorage,
        });
        if (localStorage.getItem("sb_debug_stop_auto_close")) {
          return;
        }
        window.close();
        return;
      }

      const opener =
        typeof window !== "undefined" ? (window.opener ?? null) : null;
      if (!opener) {
        const openerError =
          error ??
          "OAuth callback could not reach the window that opened it. Close this window and try again from the app.";
        setErrorMessage(openerError);
        return;
      }
      signalCompletionToShell(
        {
          error,
          integrationId: authState.integrationId || datasourceToAuth,
          oneTimeCode: authState.oneTimeCode || undefined,
        },
        { opener, targetOrigin: openerTargetOrigin },
      );

      // Errors: keep the popup open so the provider's verbatim message
      // (e.g. "access_denied: User denied the authorization request" or a
      // 401 from the integration fetch) stays visible to the user. The
      // opener still gets the failure signal via postMessage so the host UI
      // can show its own banner while the user reads the popup. Tested by
      // `embedding-oauth-error.spec.ts` and
      // `embedding-oauth-error-fullstack.spec.ts`.
      if (error) {
        return;
      }

      // NOTE that this just closes the window when the window has been
      // automatically opened.  If a user copies the OAuth URL and manually opens
      // it in a new tab, this will not close the window.
      if (
        typeof window !== "undefined" &&
        window.localStorage.getItem("sb_debug_stop_auto_close")
      ) {
        return;
      }

      window.close();
    },
    [
      authState.integrationId,
      authState.oneTimeCode,
      datasourceToAuth,
      handoffMode,
      openerTargetOrigin,
    ],
  );

  const errorCaseAndFinish = useCallback(
    (error: any) => {
      logger.warn(`Login Error: ${error}`);
      setErrorMessage(error);
      finish(
        handoffMode === "localStorage"
          ? `Authentication failed: ${error}`
          : error,
      );
    },
    [finish, handoffMode],
  );

  // Load the relevant datasource auth config async.
  const [datasource, setDatasource] = useState<
    SupersetIntegrationDto | undefined
  >();
  const dsConfig = useMemo(() => {
    return resolveOAuthCallbackConfig({
      datasource,
      profileId,
      authState,
    });
  }, [datasource, profileId, authState]);

  const [dsLoadError, setDsLoadError] = useState<string>();
  const [dsLoadState, setDsLoadState] = useState<
    "idle" | "loading" | "loaded" | "error"
  >("idle");
  useEffect(() => {
    // Wait for the shell handshake to settle before fetching the
    // integration: in storage-partitioned embed contexts the popup's
    // localStorage view is empty, so an early fetch with no bearer
    // token always returns 403 and the user gets a transient error
    // banner before the real fetch (with the shell-supplied token)
    // succeeds. Once the handshake resolves (with or without a
    // token), `oauthAccessToken` is stable.
    if (handoffMode === "postMessage" && !shellTokenResolved) return;
    if (
      handoffMode === "postMessage" &&
      authState.externalUser &&
      !oauthAccessToken
    ) {
      return;
    }
    const loadDS = async () => {
      setDsLoadState("loading");
      try {
        const datasource = await fetchOAuthCallbackIntegration(
          datasourceToAuth,
          profile?.key,
          oauthAccessToken,
        );
        if (datasource) {
          setDsLoadError(undefined);
          setDatasource(datasource);
          setDsLoadState("loaded");
        } else {
          setDsLoadError("Integration request did not return a datasource.");
          setDsLoadState("error");
        }
      } catch (err) {
        logger.error(`Failed to load integration: ${err}`);
        setDsLoadError(String(err));
        setDsLoadState("error");
      }
    };
    loadDS();
  }, [
    authState.externalUser,
    datasourceToAuth,
    handoffMode,
    oauthAccessToken,
    profile?.key,
    shellTokenResolved,
  ]);

  const loginWithTokenAndFinish = useCallback(
    async (dsConfig: ResolvedDatasourceConfig) => {
      // Login with token.
      const authId = getAuthIdFromConfig(datasourceToAuth, dsConfig);
      await loginDatasourceV3(
        profile && datasourceToAuth // fallback to controller if orch reqs arent met
          ? {
              orchestrator: true,
              integrationId: datasourceToAuth,
              profile,
              token: token ?? undefined,
              expiryTimestamp,
              agents,
              organization,
              enableDataPlaneGateway,
              profileKey: profile?.key,
            }
          : {
              orchestrator: false,
              agents,
              organization,
              body: {
                authType: IntegrationAuthType.OAUTH2_IMPLICIT,
                authId,
                token,
                expiryTimestamp,
              },
            },
      );
      finish();
    },
    [
      agents,
      datasourceToAuth,
      enableDataPlaneGateway,
      expiryTimestamp,
      finish,
      organization,
      profile,
      token,
    ],
  );

  const failExchangeCode = useCallback(
    (message: string) => {
      logger.warn(`Login Error: ${message}`);
      setErrorMessage(message);
      reportExchangeCodeFailure(message, {
        notifyFinished: finish,
      });
    },
    [finish],
  );

  const loginWithAuthCodeAndFinish = useCallback(
    async (dsConfig: ResolvedDatasourceConfig) => {
      // Login with access code.
      try {
        if (
          validateOneTimeCodeIfApplicable(
            authState,
            dsConfig.authConfig,
            handoffMode,
            authState.externalUser && Boolean(oauthAccessToken),
          )
        ) {
          const redirectInitiatedByEmbedUser = authState.externalUser;
          const useServerEndpoint =
            dsConfig.authConfig?.refreshTokenFromServer ||
            redirectInitiatedByEmbedUser;
          const authId = getAuthId(
            dsConfig.authType,
            dsConfig.authConfig,
            dsConfig.integrationId,
            dsConfig.configurationId,
          );
          if (
            redirectFinishedRef.current ||
            (handoffMode === "localStorage" &&
              localStorage.getItem(LS_OAUTH_REDIRECT_FINISHED) === "true")
          ) {
            return;
          }
          const integrationId = redirectInitiatedByEmbedUser
            ? dsConfig.integrationId
            : undefined;
          const configurationId = redirectInitiatedByEmbedUser
            ? dsConfig.configurationId
            : undefined;
          const exchangeCodeResp = useServerEndpoint
            ? await doExchangeCodeOnServer(
                authId,
                dsConfig.authType,
                dsConfig.authConfig,
                accessCode as string,
                dsConfig.pluginId,
                scope,
                integrationId,
                configurationId,
                oauthAccessToken,
              )
            : await doExchangeCodeOnAgent(
                dsConfig.integrationId,
                profile,
                accessCode as string,
                agents,
                organization,
                datasource,
                environment,
                dsConfig,
                mode,
                enableDataPlaneGateway,
                profile?.key,
              );
          if (
            (isExchangeCodeResponse(exchangeCodeResp) &&
              !exchangeCodeResp.successful) ||
            (!isExchangeCodeResponse(exchangeCodeResp) &&
              !exchangeCodeResp.success)
          ) {
            failExchangeCode(
              `Failed to exchange an authorization code for an access token: ${exchangeCodeResp.error}.`,
            );
            return;
          }
        } else {
          failExchangeCode(
            "Failed to exchange an authorization code for an access token. One time code in state parameter did not match.",
          );
          return;
        }
      } catch (err) {
        failExchangeCode(
          `Failed to exchange an authorization code for an access token: ${err}.`,
        );
        return;
      }
      finish();
    },
    [
      accessCode,
      agents,
      datasource,
      enableDataPlaneGateway,
      environment,
      failExchangeCode,
      finish,
      handoffMode,
      mode,
      organization,
      profile,
      oauthAccessToken,
      scope,
      authState,
    ],
  );

  // THIS IS THE MAIN ENTRY POINT
  useEffect(() => {
    (async () => {
      if (paramError) {
        // Check for error cases that need to be propagated from earlier in the
        // flow.
        return errorCaseAndFinish(paramError);
      }

      if (handoffMode === "localStorage" && !localStorage.isSupported()) {
        return errorCaseAndFinish(
          "This integration requires local storage to be enabled. Please enable local storage and try again.",
        );
      }

      // Wait for the shell postMessage handshake to settle. In
      // storage-partitioned embed contexts this is the only way to
      // obtain the bearer token; running the rest of the flow
      // before it resolves would issue an unauthenticated request,
      // trip the error branch, and surface a spurious failure to the
      // user mid-flow.
      if (handoffMode === "postMessage" && !shellTokenResolved) {
        return;
      }

      if (
        handoffMode === "postMessage" &&
        authState.externalUser &&
        !oauthAccessToken
      ) {
        const opener =
          typeof window !== "undefined" ? (window.opener ?? null) : null;
        if (!opener || !authState.oneTimeCode) {
          return errorCaseAndFinish(
            "OAuth callback could not reach the embedding app. Close this window and try again from the app.",
          );
        }
        return errorCaseAndFinish(
          "OAuth callback did not receive an authentication token from the embedding app. Close this window and try again.",
        );
      }

      // Check that all of the dependencies are loaded. Return will wait for
      // them to load (async).
      if (agentsLoading) {
        logger.info("No agents found.");
        return;
      } else if (dsLoadError && !dsConfig) {
        return errorCaseAndFinish(
          `Failed to load integration configuration: ${dsLoadError}`,
        );
      } else if (!dsConfig) {
        if (datasource) {
          return errorCaseAndFinish(
            "Integration loaded but no matching configuration was found.",
          );
        }
        if (dsLoadState === "idle" || dsLoadState === "loading") {
          logger.info("Datasource not found");
          return;
        }
        return errorCaseAndFinish("Failed to load integration configuration.");
      }

      // Attempt to send the request but another request already sent or the redirect has already finished, no need to send another request.
      if (
        sentRequest.current ||
        redirectFinishedRef.current ||
        (handoffMode === "localStorage" &&
          localStorage.getItem(LS_OAUTH_REDIRECT_FINISHED) === "true")
      ) {
        return;
      }

      sentRequest.current = true;
      if (!isEmpty(token)) {
        await loginWithTokenAndFinish(dsConfig);
      } else if (!isEmpty(accessCode)) {
        await loginWithAuthCodeAndFinish(dsConfig);
      } else {
        errorCaseAndFinish("Expected either an access token or code.");
      }
    })();
  }, [
    token,
    accessCode,
    authState,
    paramError,
    agentsLoading,
    datasource,
    dsConfig,
    dsLoadError,
    dsLoadState,
    errorCaseAndFinish,
    handoffMode,
    loginWithTokenAndFinish,
    loginWithAuthCodeAndFinish,
    oauthAccessToken,
    shellTokenResolved,
  ]);

  // If there is an error to show, we don't close this screen so we prioritize
  // showing that.
  return (
    <>
      {errorMessage ??
        (isLoading
          ? "Logging in..."
          : "Login complete. This window can be closed.")}
    </>
  );
};

async function doExchangeCodeOnServer(
  authId: AuthId,
  authType: AuthType,
  authConfig: AuthConfig,
  accessCode: string,
  pluginId: string,
  scope: string | null,
  integrationId: string | undefined,
  configurationId: string | undefined,
  accessToken: string | undefined,
) {
  return await exchangeOAuthCallbackCode(
    {
      authId: authId,
      authType: authType,
      authConfig: authConfig,
      accessCode: accessCode,
      pluginId: pluginId,
      origin: window.location.origin,
      grantedScope: scope,
      integrationId,
      configurationId,
    },
    accessToken,
  );
}

async function doExchangeCodeOnAgent(
  integrationId: string,
  profile: Profile | undefined,
  accessCode: string,
  agents: Agent[],
  organization: Organization,
  datasource: any,
  environment: string,
  dsConfig: ResolvedDatasourceConfig,
  mode: ViewMode | undefined,
  enableDataPlaneGateway?: boolean,
  profileKey?: string,
) {
  return await exchangeCodeV3(
    integrationId && profile && accessCode
      ? {
          orchestrator: true,
          agents,
          organization,
          integrationId:
            dsConfig.configSource === "server" ? datasource?.id : undefined,
          configurationId: dsConfig.configurationId,
          accessCode,
          profile,
          authType: dsConfig.authType,
          authConfig: dsConfig.authConfig,
          enableDataPlaneGateway,
          profileKey,
        }
      : {
          orchestrator: false,
          agents,
          organization,
          body: {
            accessCode,
            datasourceId: datasource?.id,
            environment: environment,
            authType: dsConfig.authType,
            authConfig: dsConfig.authConfig,
          },
          mode,
          profile,
        },
  );
}

function validateOneTimeCodeIfApplicable(
  state: DatasourceOneTimeState,
  authConfig: AuthConfig,
  handoffMode: "localStorage" | "postMessage",
  postMessageOneTimeCodeVerified: boolean,
): boolean {
  if (!state) {
    return false;
  }
  return validateOAuthCallbackOneTimeCodeIfApplicable({
    authConfig,
    handoffMode,
    postMessageOneTimeCodeVerified,
    state,
    storage: localStorage,
  });
}

const EMPTY_OAUTH_REDIRECT_STATE: DatasourceOneTimeState = {
  externalUser: false,
  integrationId: "",
  oneTimeCode: "",
};

/** OAuth state is carried in the redirect URL `state` query param (base64 JSON). */
function getOAuthStateFromRedirect(
  search: Search,
): DatasourceOneTimeState | DatasourceAuthState {
  const stateValue = new URLSearchParams(search).get("state");
  const parsedState = stateValue ? parseOAuthState(stateValue) : undefined;
  if (parsedState?.useLocalStorage) {
    const fullState = localStorage.getItem(LS_FULL_STATE);
    if (fullState) {
      return parseOAuthState(fullState) ?? parsedState;
    }
    return parsedState;
  }
  if (parsedState) {
    return parsedState;
  }
  const legacyIntegrationId = localStorage.getItem(LS_OAUTH_DATASOURCE_KEY);
  const legacyOneTimeCode = localStorage.getItem(LS_OAUTH_ONE_TIME_CODE);
  if (legacyIntegrationId || legacyOneTimeCode) {
    return {
      externalUser: false,
      integrationId: legacyIntegrationId ?? "",
      oneTimeCode: legacyOneTimeCode ?? "",
      useLocalStorage: true,
    };
  }
  return EMPTY_OAUTH_REDIRECT_STATE;
}

function parseOAuthState(
  stateValue: string,
): DatasourceOneTimeState | DatasourceAuthState | undefined {
  try {
    return JSON.parse(atob(stateValue)) as
      | DatasourceOneTimeState
      | DatasourceAuthState;
  } catch {
    logger.warn(`Failed to parse state string`);
    return undefined;
  }
}

export default OAuthCallback;
